import React, { useCallback, useContext, useMemo, useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import DataTable from "../../../../../components/DataTable";
import { AuthContext } from "../../../../../helpers/AuthContext";
import "./InstancePromoStoreUsers.css";
import PromoStoreAddonConnection from "../design/PromoStoreAddonConnection";
import PromoStoreEmployeeDirectory, { employeeActivationLabel } from "./PromoStoreEmployeeDirectory";
import PromoStoreFinance from "./PromoStoreFinance";
import PromoStorePointsHistory, { usePromoStoreUserPoints } from "./PromoStoreUserPoints";
import PromoStoreUserBudgets, { budgetColumn, usePromoStoreUserBudgets } from "./PromoStoreUserBudgets";
import PromoStoreBudgetReport from "./PromoStoreBudgetReport";
import PromoStorePointsReport from "./PromoStorePointsReport";

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function normalizeRoles(roles) {
  if (!roles) return "—";
  if (Array.isArray(roles)) return roles.length ? roles.join(", ") : "—";
  return String(roles);
}

function isLinked(user) {
  return Boolean(user?.linkedEliteUserId);
}

function formatLocationLabel(location) {
  if (!location) return "—";
  return location?.label || location?.name || location?.key || "—";
}

function formatLocationList(locations) {
  if (!Array.isArray(locations) || locations.length === 0) return "—";
  return locations.map((location) => formatLocationLabel(location)).join(", ");
}

const InstancePromoStoreUsers = ({ instanceUniqueId, storeId, isActive }) => {
  const { authState } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [employeeState, setEmployeeState] = useState(null);
  const [employeeRefresh, setEmployeeRefresh] = useState(0);
  const usersScope = `${instanceUniqueId}:${storeId}`;
  const currentUsersScope = useRef(usersScope);
  currentUsersScope.current = usersScope;
  const refreshEmployees = useCallback(() => setEmployeeRefresh((value) => value + 1), []);
  const employeeEnabled = employeeState?.scope === `${instanceUniqueId}:${storeId}` && employeeState.enabled;
  const employeeLinks = employeeEnabled ? employeeState.links : {};
  const [userAddressesEnabled, setUserAddressesEnabled] = useState(false);
  const [locationAddressOptions, setLocationAddressOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [sendingReset, setSendingReset] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [resetNotice, setResetNotice] = useState(null); // { type: "success"|"error", text: string }  
  const autoRefreshKeyRef = useRef("");
  const [pointsRevision, setPointsRevision] = useState(0);
  const pointsState = usePromoStoreUserPoints({ storeId, instanceUniqueId, isActive, users, refreshKey: `${employeeRefresh}:${pointsRevision}` });
  const [budgetRevision, setBudgetRevision] = useState(0);
  const budgetState = usePromoStoreUserBudgets({ storeId, instanceUniqueId, isActive, users, refreshKey: `${employeeRefresh}:${budgetRevision}` });

  // ✅ create user modal
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createForm, setCreateForm] = useState({
    email: "",
    username: "",
    displayName: "",
    role: "customer",
    password: "",
    locationAddressKeys: [],
  });

  useEffect(() => {
    setUsers([]); setSelectedUser(null); setUserAddressesEnabled(false);
    setLocationAddressOptions([]); setLoading(false); setRefreshing(false); setSavingLocation(false); setResetNotice(null);
  }, [usersScope]);

  const canViewRaw =
    ["administrator", "super-administrator"].includes(authState?.role) ||
    authState?.role === "admin" ||
    authState?.role === "superadmin";

  // Optional: restrict who can create WP users
  const canCreateUsers =
    ["administrator", "super-administrator"].includes(authState?.role) ||
    authState?.role === "admin" ||
    authState?.role === "superadmin";

  const fetchUsers = useCallback(async () => {
    if (!instanceUniqueId || !storeId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${instanceUniqueId}/${storeId}/wp-users`
      );
      if (currentUsersScope.current !== `${instanceUniqueId}:${storeId}`) return;
      setUsers(resp.data?.users || []);
      setUserAddressesEnabled(resp.data?.userAddressesEnabled === true);
      setLocationAddressOptions(resp.data?.locationAddressOptions || []);
      refreshEmployees();
    } catch (err) {
      if (currentUsersScope.current !== `${instanceUniqueId}:${storeId}`) return;
      console.error("Failed to load PromoStore users:", err);
      setError("Unable to load users from Elite Core.");
    } finally {
      if (currentUsersScope.current === `${instanceUniqueId}:${storeId}`) setLoading(false);
    }
  }, [instanceUniqueId, storeId, refreshEmployees]);

  const refreshUsers = useCallback(async () => {
    if (!instanceUniqueId || !storeId) return;
    setRefreshing(true);
    setError(null);
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${instanceUniqueId}/${storeId}/wp-users/refresh`
      );
      if (currentUsersScope.current !== `${instanceUniqueId}:${storeId}`) return;
      setUsers(resp.data?.users || []);
      setUserAddressesEnabled(resp.data?.userAddressesEnabled === true);
      setLocationAddressOptions(resp.data?.locationAddressOptions || []);
      refreshEmployees();
    } catch (err) {
      if (currentUsersScope.current !== `${instanceUniqueId}:${storeId}`) return;
      console.error("Failed to refresh PromoStore users:", err);
      setError(err?.response?.data?.message || "Failed to refresh users from WordPress.");
    } finally {
      if (currentUsersScope.current === `${instanceUniqueId}:${storeId}`) setRefreshing(false);
    }
  }, [instanceUniqueId, storeId, refreshEmployees]);

  const openCreate = useCallback(() => {
    setCreateError(null);
    setCreateForm({
      email: "",
      username: "",
      displayName: "",
      role: "customer",
      password: "",
      locationAddressKeys: [],
    });
    setCreateOpen(true);
  }, []);

  const closeCreate = useCallback(() => {
    if (creating) return;
    setCreateOpen(false);
  }, [creating]);

  const submitCreate = useCallback(async () => {
    if (!instanceUniqueId || !storeId) return;

    setCreating(true);
    setCreateError(null);

    try {
      const payload = {
        email: createForm.email?.trim(),
        username: createForm.username?.trim() || undefined,
        displayName: createForm.displayName?.trim() || undefined,
        role: createForm.role,
        password: createForm.password,
        locationAddressKeys: Array.isArray(createForm.locationAddressKeys) ? createForm.locationAddressKeys : [],
      };

      if (!payload.email) throw new Error("Email is required.");
      if (!["customer", "administrator"].includes(payload.role))
        throw new Error("Role must be customer or administrator.");
      if (!payload.password || payload.password.length < 8)
        throw new Error("Password must be at least 8 characters.");

      const resp = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${instanceUniqueId}/${storeId}/wp-users/create`,
        payload
      );

      // best: backend returns updated list
      const nextUsers = resp.data?.users;
      if (Array.isArray(nextUsers)) {
        setUsers(nextUsers);
        setUserAddressesEnabled(resp.data?.userAddressesEnabled === true);
        setLocationAddressOptions(resp.data?.locationAddressOptions || []);
      } else {
        // fallback: refresh
        await refreshUsers();
      }

      setCreateOpen(false);
    } catch (err) {
      console.error("Failed to create WP user:", err);
      setCreateError(err?.response?.data?.message || err?.message || "Failed to create user.");
    } finally {
      setCreating(false);
    }
  }, [instanceUniqueId, storeId, createForm, refreshUsers]);

  const sendPasswordReset = useCallback(async () => {
    if (!instanceUniqueId || !storeId || !selectedUser?.wpUserId) return;
  
    setSendingReset(true);
    setResetNotice(null);
  
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${instanceUniqueId}/${storeId}/wp-users/${selectedUser.wpUserId}/send-password-reset`
      );
  
      if (resp.data?.success) {
        setResetNotice({ type: "success", text: resp.data?.message || "Password reset email sent." });
      } else {
        setResetNotice({ type: "error", text: resp.data?.message || "Failed to send password reset email." });
      }
    } catch (err) {
      setResetNotice({
        type: "error",
        text: err?.response?.data?.message || "Failed to send password reset email.",
      });
    } finally {
      setSendingReset(false);
    }
  }, [instanceUniqueId, storeId, selectedUser]);  

  const updateAssignedLocation = useCallback(async (nextLocationAddressKeys) => {
    if (!instanceUniqueId || !storeId || !selectedUser?.wpUserId) return;

    setSavingLocation(true);
    setError(null);
    try {
      const resp = await axios.put(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${instanceUniqueId}/${storeId}/wp-users/${selectedUser.wpUserId}/location`,
        {
          locationAddressKeys: Array.isArray(nextLocationAddressKeys) ? nextLocationAddressKeys : [],
        }
      );

      const updatedUser = resp.data?.user;
      if (currentUsersScope.current !== `${instanceUniqueId}:${storeId}`) return;
      if (!updatedUser) return;

      setUsers((current) =>
        current.map((user) => (Number(user.wpUserId) === Number(updatedUser.wpUserId) ? updatedUser : user))
      );
      setSelectedUser(updatedUser);
      refreshEmployees();
    } catch (err) {
      if (currentUsersScope.current !== `${instanceUniqueId}:${storeId}`) return;
      console.error("Failed to update PromoStore user location:", err);
      setError(err?.response?.data?.message || "Failed to update assigned location.");
    } finally {
      if (currentUsersScope.current === `${instanceUniqueId}:${storeId}`) setSavingLocation(false);
    }
  }, [instanceUniqueId, selectedUser, storeId, refreshEmployees]);

  useEffect(() => {
    if (!isActive || !instanceUniqueId || !storeId) return;

    let cancelled = false;
    const refreshKey = `${instanceUniqueId}:${storeId}`;

    const loadCachedThenRefresh = async () => {
      await fetchUsers();
      if (cancelled || autoRefreshKeyRef.current === refreshKey) return;
      autoRefreshKeyRef.current = refreshKey;
      await refreshUsers();
    };

    loadCachedThenRefresh();

    return () => {
      cancelled = true;
    };
  }, [fetchUsers, instanceUniqueId, isActive, refreshUsers, storeId]);

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const q = search.toLowerCase();
    return users.filter((user) => {
      const login = user?.userLogin?.toLowerCase() || "";
      const name = user?.displayName?.toLowerCase() || "";
      const email = user?.email?.toLowerCase() || "";
      const employeeIds = (employeeLinks[user.wpUserId] || []).map((record) => record.employeeId.toLowerCase());
      return login.includes(q) || name.includes(q) || email.includes(q) || employeeIds.some((id) => id.includes(q));
    });
  }, [search, users, employeeLinks]);

  const rows = useMemo(
    () =>
      filteredUsers.map((user, index) => ({
        ...user,
        id: user.id || `wp-${user.wpUserId}`,
        rowNumber: index + 1,
      })),
    [filteredUsers]
  );

  const columns = useMemo(
    () => [
      { field: "rowNumber", headerName: "#", width: 70, align: "center", headerAlign: "center" },
      { field: "wpUserId", headerName: "WP ID", width: 90, align: "center", headerAlign: "center" },
      ...(pointsState.enabled ? [{ field: "pointsBalance", headerName: `${pointsState.label || "Points"} balance`, width: 170, align: "right", headerAlign: "right", valueGetter: (_value, row) => pointsState.loading ? "Loading…" : pointsState.error ? "Unavailable" : pointsState.balances[row.wpUserId]?.balance?.toLocaleString() ?? "Unavailable" }] : []),
      ...(budgetState.enabled ? [
        { field: "budgetScope", headerName: "Budget scope", width: 180, valueGetter: () => budgetState.mode === "individual" ? "Individual per location" : "Shared location" },
        ...[["limit", "Budget limit"], ["spent", "Budget spent"], ["remaining", "Budget remaining"]].map(([field, headerName]) => ({ field: `budget_${field}`, headerName, width: 250, valueGetter: (_value, row) => budgetColumn(budgetState, row.wpUserId, field) })),
      ] : []),
      ...(employeeEnabled ? [
        { field: "employeeId", headerName: "Employee ID", width: 180, valueGetter: (_value, row) => employeeState.loading ? "Loading…" : employeeState.error ? "Unavailable" : (employeeLinks[row.wpUserId] || []).map((record) => record.employeeId).join(", ") || "—" },
        { field: "employeeActivation", headerName: "Employee activation", width: 190, valueGetter: (_value, row) => employeeState.loading ? "Loading…" : employeeState.error ? "Unavailable" : (employeeLinks[row.wpUserId] || []).map(employeeActivationLabel).join(", ") || "No claimed ID" },
      ] : []),
      {
        field: "displayName",
        headerName: "Name",
        width: 200,
        align: "left",
        headerAlign: "left",
        valueGetter: (value, row) => row?.displayName || row?.userLogin || "—",
      },
      {
        field: "email",
        headerName: "Email",
        width: 220,
        align: "left",
        headerAlign: "left",
        valueGetter: (value, row) => row?.email || "—",
      },
      {
        field: "roles",
        headerName: "Roles",
        width: 180,
        align: "left",
        headerAlign: "left",
        valueGetter: (value, row) => normalizeRoles(row?.roles),
      },
      {
        field: "location",
        headerName: "Location",
        width: 260,
        align: "left",
        headerAlign: "left",
        valueGetter: (value, row) => formatLocationList(row?.locationAddresses),
      },
      {
        field: "registeredAt",
        headerName: "Registered",
        width: 160,
        valueGetter: (value, row) => formatDate(row?.registeredAt),
      },
      {
        field: "linked",
        headerName: "Linked",
        width: 120,
        valueGetter: (value, row) => (isLinked(row) ? "Linked" : "Not linked"),
      },
      { field: "orderCount", headerName: "Orders", width: 110, align: "center", headerAlign: "center", valueGetter: () => "—" },
      { field: "totalSpend", headerName: "Total Spent", width: 140, align: "right", headerAlign: "right", valueGetter: () => "—" },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={(event) => {
              event.stopPropagation();
              setSelectedUser(params.row);
            }}
            className="promostore-users-view-button"
          >
            View
          </Button>
        ),
      },
    ],
    [employeeEnabled, employeeState, employeeLinks, pointsState, budgetState]
  );

  return (
    <Box className="promostore-users-tab" sx={{ mt: 2 }}>
      <PromoStoreAddonConnection
        storeId={storeId}
        instanceUniqueId={instanceUniqueId}
        isActive={isActive}
        surface="users"
        onSaved={refreshEmployees}
      />
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Search users"
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ width: { xs: "100%", md: 320 } }}
        />
        <Box sx={{ flex: 1 }} />
        <PromoStoreBudgetReport key={usersScope} users={users} state={budgetState} usersBusy={loading || refreshing} usersError={error} onRefresh={() => setBudgetRevision((value) => value + 1)} />
        <PromoStorePointsReport key={`points:${usersScope}`} users={users} state={pointsState} usersBusy={loading || refreshing} usersError={error} onRefresh={() => setPointsRevision((value) => value + 1)} />

        {canCreateUsers && (
          <Button variant="outlined" onClick={openCreate} sx={{ whiteSpace: "nowrap" }}>
            Create User
          </Button>
        )}

        <Button
          variant="contained"
          onClick={refreshUsers}
          disabled={refreshing}
          sx={{
            whiteSpace: "nowrap",
            bgcolor: "#c3171d",
            "&:hover": { bgcolor: "#a81218" },
          }}
        >
          {refreshing ? "Refreshing…" : "Refresh from WordPress"}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {pointsState.error && <Alert severity="error" sx={{ mb: 2 }}>{pointsState.error} Use Refresh from WordPress to retry.</Alert>}
      {budgetState.error && <Alert severity="error" sx={{ mb: 2 }}>{budgetState.error} Use Refresh from WordPress to retry.</Alert>}
      {budgetState.configured && !budgetState.enabled && budgetState.blockedBy?.length > 0 && <Alert severity="info" sx={{ mb: 2 }}>Enable Location Addresses to view this store’s budgets.</Alert>}
      {budgetState.enabled && <Typography variant="body2" sx={{ mb: 2 }}>Budget period: {budgetState.period} · {budgetState.mode === "individual" ? "Individual per location" : "Shared location"} · {budgetState.currency || "Store currency unavailable"}</Typography>}
      {pointsState.enabled && !pointsState.loading && Object.values(pointsState.balances || {}).some((value) => value.source === "legacy") && <Alert severity="info" sx={{ mb: 2 }}>Some balances use WordPress’s shared legacy fallback. Site balances take precedence; these reads do not migrate balances.</Alert>}

      {refreshing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Refreshing users from WordPress. Cached users remain visible while the refresh completes.
        </Alert>
      )}

      <Box className="promostore-users-table-card">
        <DataTable
          rows={rows}
          columns={columns}
          loading={loading}
          rowCount={rows.length}
          pageSize={10}
          onRowClick={(params) => setSelectedUser(params.row)}
          minHeight={420}
          disableSelectionOnClick
          localeText={{
            footerTotalRows: "Total users:",
            MuiTablePagination: {
              labelRowsPerPage: "Rows per page",
              labelDisplayedRows: ({ from, to, count }) =>
                `Showing ${from}–${to} of ${count !== -1 ? count : `more than ${to}`} users`,
            },
          }}
        />
      </Box>

      <PromoStoreEmployeeDirectory
        storeId={storeId} instanceUniqueId={instanceUniqueId} isActive={isActive}
        users={users} refreshKey={employeeRefresh} onChange={setEmployeeState} onViewUser={setSelectedUser}
        canImport={authState?.role === "administrator"}
      />

      {/* ✅ Create User Modal */}
      <Dialog open={createOpen} onClose={closeCreate} fullWidth maxWidth="sm">
        <DialogTitle>Create WordPress User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {createError && <Alert severity="error">{createError}</Alert>}

            <TextField
              label="Email"
              value={createForm.email}
              onChange={(e) => setCreateForm((s) => ({ ...s, email: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Role"
              select
              value={createForm.role}
              onChange={(e) => setCreateForm((s) => ({ ...s, role: e.target.value }))}
              fullWidth
            >
              <MenuItem value="customer">Customer</MenuItem>
              <MenuItem value="administrator">Administrator</MenuItem>
            </TextField>

            <TextField
              label="Password"
              type="password"
              value={createForm.password}
              onChange={(e) => setCreateForm((s) => ({ ...s, password: e.target.value }))}
              helperText="Minimum 8 characters"
              fullWidth
            />

            <Divider />

            <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
              Optional
            </Typography>

            <TextField
              label="Username (optional)"
              value={createForm.username}
              onChange={(e) => setCreateForm((s) => ({ ...s, username: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Display name (optional)"
              value={createForm.displayName}
              onChange={(e) => setCreateForm((s) => ({ ...s, displayName: e.target.value }))}
              fullWidth
            />

            {userAddressesEnabled && locationAddressOptions.length > 0 && (
              <Autocomplete
                multiple
                fullWidth
                options={locationAddressOptions}
                value={locationAddressOptions.filter((location) =>
                  Array.isArray(createForm.locationAddressKeys)
                    ? createForm.locationAddressKeys.includes(location.key)
                    : false
                )}
                onChange={(_event, nextValue) =>
                  setCreateForm((s) => ({
                    ...s,
                    locationAddressKeys: nextValue.map((location) => location.key),
                  }))
                }
                getOptionLabel={(option) => option?.label || option?.key || ""}
                isOptionEqualToValue={(option, value) => option.key === value.key}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned Locations (optional)"
                    helperText="Optionally pre-assign one or more configured PromoStore locations."
                  />
                )}
              />
            )}

            {userAddressesEnabled && locationAddressOptions.length === 0 && (
              <Typography variant="body2" sx={{ color: "#777" }}>
                No Location Addresses configured.
              </Typography>
            )}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={closeCreate} disabled={creating}>
                Cancel
              </Button>
              <Button variant="contained" onClick={submitCreate} disabled={creating}>
                {creating ? "Creating…" : "Create"}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Existing user details dialog */}
      <Dialog open={Boolean(selectedUser)} onClose={() => setSelectedUser(null)} fullWidth maxWidth="sm">
        <DialogTitle>WordPress User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">WP User ID</Typography>
                <Typography>{selectedUser.wpUserId}</Typography>
              </Stack>
              {employeeEnabled && <Stack spacing={0.5}>
                <Typography variant="subtitle2">Employee IDs</Typography>
                {employeeState.loading ? <Typography>Loading…</Typography> : employeeState.error ? <Typography>Employee links unavailable. Refresh the roster to retry.</Typography> : (employeeLinks[selectedUser.wpUserId] || []).length ? (employeeLinks[selectedUser.wpUserId] || []).map((record) => <Typography key={record.id}>{record.employeeId} · {employeeActivationLabel(record)} · {record.status}{record.claimedAt ? ` · Claimed ${record.claimedAt}` : ""}</Typography>) : <Typography>No claimed employee ID</Typography>}
              </Stack>}
              {authState?.role === "administrator" && <PromoStoreFinance key={`${usersScope}:${selectedUser.wpUserId}`} storeId={storeId} instanceUniqueId={instanceUniqueId} userId={selectedUser.wpUserId} balance={pointsState.enabled ? pointsState.balances?.[selectedUser.wpUserId]?.balance : undefined} policy={pointsState.enabled ? pointsState.balances?.[selectedUser.wpUserId]?.policy : undefined} locations={budgetState.enabled ? budgetState.users?.[selectedUser.wpUserId] || [] : []} budgetMode={budgetState.mode} onSaved={() => { setPointsRevision(value => value + 1); setBudgetRevision(value => value + 1); }} />}
              <PromoStorePointsHistory storeId={storeId} instanceUniqueId={instanceUniqueId} userId={selectedUser.wpUserId} enabled={Boolean(pointsState.enabled)} refreshKey={`${employeeRefresh}:${pointsRevision}`} />
              <PromoStoreUserBudgets state={budgetState} userId={selectedUser.wpUserId} onRefresh={() => setBudgetRevision((value) => value + 1)} />

              {resetNotice && (
                <Alert severity={resetNotice.type} sx={{ mb: 1 }}>
                  {resetNotice.text}
                </Alert>
              )}

              <Button
                variant="outlined"
                onClick={sendPasswordReset}
                disabled={sendingReset || !selectedUser?.email}
                sx={{ alignSelf: "flex-start" }}
              >
                {sendingReset ? "Sending…" : "Send password reset"}
              </Button>

              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Username</Typography>
                <Typography>{selectedUser.userLogin || "—"}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Display Name</Typography>
                <Typography>{selectedUser.displayName || "—"}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Email</Typography>
                <Typography>{selectedUser.email || "—"}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Roles</Typography>
                <Typography>{normalizeRoles(selectedUser.roles)}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Registered</Typography>
                <Typography>{formatDate(selectedUser.registeredAt)}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Status</Typography>
                <Typography>{selectedUser.raw?.status || "active"}</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Last Login</Typography>
                <Typography>
                  {selectedUser.raw?.lastLoginAt ? formatDate(selectedUser.raw.lastLoginAt) : "—"}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Linked Elite User</Typography>
                <Typography>{isLinked(selectedUser) ? "Linked" : "Not linked"}</Typography>
              </Stack>
              {userAddressesEnabled && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">Assigned Location</Typography>
                  <TextField
                    select
                    size="small"
                    value={Array.isArray(selectedUser.locationAddressKeys) ? selectedUser.locationAddressKeys : []}
                    onChange={(event) => updateAssignedLocation(event.target.value)}
                    disabled={savingLocation}
                    helperText={savingLocation ? "Saving location assignment..." : "Assign this WordPress user to one or more configured PromoStore locations."}
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (selected) => {
                        if (!Array.isArray(selected) || selected.length === 0) {
                          return "None / Unassigned";
                        }
                        const selectedSet = new Set(selected);
                        return locationAddressOptions
                          .filter((location) => selectedSet.has(location.key))
                          .map((location) => location.label)
                          .join(", ");
                      },
                    }}
                    fullWidth
                  >
                    {locationAddressOptions.map((location) => (
                      <MenuItem key={location.key} value={location.key}>
                        {location.subtitle ? `${location.label} — ${location.subtitle}` : location.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              )}
              {!userAddressesEnabled && Array.isArray(selectedUser.locationAddresses) && selectedUser.locationAddresses.length > 0 && (
                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">Assigned Locations</Typography>
                  <Typography>{formatLocationList(selectedUser.locationAddresses)}</Typography>
                </Stack>
              )}
              <Divider />
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Order Count</Typography>
                <Typography>—</Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Total Spend</Typography>
                <Typography>—</Typography>
              </Stack>

              {canViewRaw && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700 }}>Raw JSON</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <pre
                      style={{
                        fontSize: 11,
                        background: "#111",
                        color: "#0f0",
                        padding: 8,
                        borderRadius: 8,
                        overflow: "auto",
                        maxHeight: 320,
                        margin: 0,
                      }}
                    >
                      {JSON.stringify(selectedUser.raw || {}, null, 2)}
                    </pre>
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default InstancePromoStoreUsers;
